// AdminTMHubPage.js — Phase TM-4 (Testing & Marketing Hub, read-only list + detail)
//
// Admin page at /admin/tm. Shows every demo/test run across both Macs.
//   - Live read of the `tm_runs` Firestore collection
//   - Filter tabs by publishStatus + status
//   - Detail drawer with 6 sampled thumbnails, scene-QA table, surfacedBugs
//   - Video player when videoURL is populated (post-Promote in TM-5)
//
// Phase TM-5 will add the Trigger / Approve / Discard buttons backed by
// Cloud Functions. This file is intentionally read-only for now so it can
// ship + start populating with data while the queue/promote layer lands.
//
// Auth: same UID allowlist + custom-claim pattern as TemplateCandidatesAdminPage.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import './App.css';

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'];

const STATUS_FILTERS = [
  { id: 'all',          label: 'All',          predicate: () => true },
  { id: 'draft',        label: 'Draft',        predicate: (r) => (r.publishStatus || 'draft') === 'draft' },
  { id: 'needsReview',  label: 'Needs Review', predicate: (r) => r.status === 'needs-review' || r.status === 'failed' },
  { id: 'approved',     label: 'Approved',     predicate: (r) => r.publishStatus === 'approved' },
  { id: 'published',    label: 'Published',    predicate: (r) => r.publishStatus === 'published' },
  { id: 'discarded',    label: 'Discarded',    predicate: (r) => r.publishStatus === 'discarded' },
];

// ──────────────────────────────────────────────────────────────────────────────
// Styling helpers — dark-theme palette consistent with other /admin pages
// ──────────────────────────────────────────────────────────────────────────────
const PALETTE = {
  bg: '#0f172a', panel: '#1e293b', panelAlt: '#0b1220', border: '#334155',
  text: '#f8fafc', muted: '#94a3b8', dim: '#64748b',
  accent: '#3b82f6', success: '#10b981', warn: '#f59e0b', error: '#ef4444',
};

function qaScoreColor(score) {
  if (score == null) return PALETTE.dim;
  if (score >= 0.9) return PALETTE.success;
  if (score >= 0.7) return PALETTE.warn;
  return PALETTE.error;
}

function statusColor(status) {
  switch (status) {
    case 'passed':       return PALETTE.success;
    case 'needs-review': return PALETTE.warn;
    case 'failed':       return PALETTE.error;
    case 'running':      return PALETTE.accent;
    default:             return PALETTE.dim;
  }
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  // Firestore Timestamp objects have a .toDate() method when read via the SDK
  let d;
  if (typeof ts.toDate === 'function') d = ts.toDate();
  else if (ts.seconds) d = new Date(ts.seconds * 1000);
  else if (typeof ts === 'string') d = new Date(ts);
  else return '—';
  if (isNaN(d)) return '—';
  return d.toLocaleString();
}

function formatDuration(sec) {
  if (sec == null) return '—';
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s}s`;
}

// MM:SS formatter for the per-scene dialog timestamps in the detail drawer.
function formatTimestamp_MMSS(sec) {
  if (sec == null || isNaN(sec)) return '—:—';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ──────────────────────────────────────────────────────────────────────────────
// Detail drawer (right column when a row is selected)
// Two-column interior: media (capped width) on the left, QA + bugs on the right.
// ──────────────────────────────────────────────────────────────────────────────
function DetailDrawer({ run, onClose }) {
  const [thumbIdx, setThumbIdx] = useState(0);
  const [copied, setCopied] = useState(false);
  useEffect(() => { setThumbIdx(0); setCopied(false); }, [run?.id]);

  if (!run) return null;

  const thumbs = run.thumbnailURLs || [];
  const sceneQA = run.sceneQA || [];
  const surfacedBugs = run.surfacedBugs || [];
  const playableSrc = run.videoURL || null;
  const videoPath = run.draftDirPath ? `${run.draftDirPath.replace(/\/+$/, '')}/final.mp4` : '';
  const copyVideoPath = async () => {
    if (!videoPath) return;
    try {
      await navigator.clipboard.writeText(videoPath);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be denied — fall back to a prompt the user can select
      window.prompt('Copy this path:', videoPath);
    }
  };

  return (
    <div style={{
      background: PALETTE.panel, borderRadius: 8, padding: 20,
      border: `1px solid ${PALETTE.border}`, position: 'sticky', top: 20,
      alignSelf: 'start', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto',
    }}>
      {/* --- Header --- */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <h2 style={{ color: PALETTE.text, margin: 0, fontSize: '1.2rem' }}>{run.scenarioId || '(unknown scenario)'}</h2>
          <div style={{ color: PALETTE.dim, fontSize: '0.78rem', marginTop: 4 }}>
            {run.flowName} · runId <code>{run.id.substring(0, 8)}…</code>
          </div>
        </div>
        <button onClick={onClose} style={{
          background: 'transparent', color: PALETTE.dim, border: 'none', cursor: 'pointer',
          fontSize: '1.2rem', padding: '4px 8px',
        }}>×</button>
      </div>

      {/* --- Meta strip (full width, top) --- */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16,
        background: PALETTE.panelAlt, padding: 12, borderRadius: 6, fontSize: '0.82rem',
      }}>
        <Meta label="Machine" value={run.machineName} />
        <Meta label="Build" value={run.appBuild} />
        <Meta label="Simulator" value={run.iOSSimulator} />
        <Meta label="Duration" value={formatDuration(run.totalDurationSec)} />
        <Meta label="QA Score" value={
          <span style={{ color: qaScoreColor(run.qaScore), fontWeight: 700 }}>
            {run.qaScore != null ? `${Math.round(run.qaScore * 100)}%` : '—'}
            {' '}({run.scenesPassed || 0}/{run.scenesTotal || 0})
          </span>
        } />
        <Meta label="Status" value={
          <span style={{ color: statusColor(run.status), fontWeight: 600 }}>{run.status}</span>
        } />
        <Meta label="Trigger" value={run.triggeredBy} />
        <Meta label="Created" value={formatTimestamp(run.createdAt)} />
      </div>

      {/* --- Two-column interior: media | QA --- */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(260px, 320px) 1fr', gap: 16,
      }}>

        {/* === Media column === */}
        <div>
          {thumbs.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <SectionHeader>Thumbnails ({thumbs.length})</SectionHeader>
              <div style={{
                background: PALETTE.panelAlt, borderRadius: 6, padding: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '60vh', minHeight: 320, maxHeight: 560, overflow: 'hidden',
              }}>
                <img
                  src={thumbs[thumbIdx]} alt={`thumb ${thumbIdx + 1}`}
                  style={{
                    maxHeight: '100%', maxWidth: '100%', objectFit: 'contain',
                    borderRadius: 4, border: `1px solid ${PALETTE.border}`,
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 4, marginTop: 6, overflowX: 'auto' }}>
                {thumbs.map((url, i) => (
                  <img
                    key={url} src={url} alt={`thumb ${i + 1}`}
                    onClick={() => setThumbIdx(i)}
                    style={{
                      height: 48, cursor: 'pointer', borderRadius: 4,
                      border: `2px solid ${i === thumbIdx ? PALETTE.accent : 'transparent'}`,
                      opacity: i === thumbIdx ? 1 : 0.55,
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* --- Video --- */}
          <div>
            <SectionHeader>
              Final video
              {run.videoURL && (
                <span style={{
                  marginLeft: 8, fontSize: '0.62rem', color: PALETTE.accent,
                  background: PALETTE.accent + '22', padding: '2px 6px', borderRadius: 4,
                }}>cloud</span>
              )}
            </SectionHeader>
            {playableSrc ? (
              <video
                key={playableSrc}
                controls src={playableSrc}
                style={{ width: '100%', borderRadius: 6, background: '#000' }}
              />
            ) : (
              <div style={{
                background: PALETTE.panelAlt, color: PALETTE.dim, padding: 10, borderRadius: 6,
                fontSize: '0.78rem',
              }}>
                <div style={{ color: PALETTE.muted, marginBottom: 8 }}>
                  Video lives on the Mac that produced this run.
                </div>
                <button
                  onClick={copyVideoPath}
                  disabled={!videoPath}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 4, cursor: videoPath ? 'pointer' : 'not-allowed',
                    background: copied ? PALETTE.success : PALETTE.accent,
                    color: 'white', border: 'none', fontSize: '0.8rem', fontWeight: 600,
                    marginBottom: 8,
                  }}
                >
                  {copied ? '✓ Copied — paste in Finder (⌘⇧G)' : 'Copy video path'}
                </button>
                <div style={{ fontSize: '0.72rem', color: PALETTE.dim, lineHeight: 1.5 }}>
                  In Finder, press <code style={{ color: PALETTE.muted }}>⌘⇧G</code>, paste, hit Return — opens in QuickTime.
                </div>
                <div style={{ color: PALETTE.muted, marginTop: 8, fontFamily: 'monospace', fontSize: '0.68rem', wordBreak: 'break-all' }}>
                  {videoPath || '—'}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* === Dialog list + bugs column === */}
        <div>
          <SectionHeader>Dialog ({sceneQA.length} scenes)</SectionHeader>
          <div style={{ marginBottom: 16 }}>
            {/* Timestamps come from the test harness as offsets from demoStartTime,
                but record-and-narrate.sh trims test-runner warmup from the FRONT of
                the final video. Subtracting scene[0].startSec re-anchors so scene 1
                shows as 0:00 — matching what the viewer sees when scrubbing. */}
            {(() => null)()}
            {sceneQA.map((s, i) => {
              const ok = !s.usedFallback && (!s.missingLabels || s.missingLabels.length === 0);
              const dialog = s.actualNarration || s.intendedNarration || '';
              const anchor = (sceneQA[0]?.startSec) ?? 0;
              const adjStart = s.startSec != null ? Math.max(0, s.startSec - anchor) : null;
              const adjEnd = s.endSec != null ? Math.max(0, s.endSec - anchor) : null;
              const tsStart = adjStart != null ? formatTimestamp_MMSS(adjStart) : null;
              const tsEnd = adjEnd != null ? formatTimestamp_MMSS(adjEnd) : null;
              const tsRange = tsStart ? `${tsStart}${tsEnd ? `–${tsEnd}` : ''}` : `#${i + 1}`;
              return (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '60px 16px 1fr', gap: 8,
                  padding: '8px 0', borderBottom: `1px solid ${PALETTE.border}`,
                  alignItems: 'start',
                }}>
                  <code style={{
                    color: PALETTE.muted, fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums',
                    paddingTop: 2,
                  }}>{tsRange}</code>
                  <span style={{ color: ok ? PALETTE.success : PALETTE.warn, fontSize: '0.9rem', paddingTop: 1 }}>
                    {ok ? '✓' : (s.usedFallback ? '⚠︎' : '✕')}
                  </span>
                  <div>
                    <div style={{ color: PALETTE.muted, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                      {s.name || `Scene ${i + 1}`}
                      {s.usedFallback && (
                        <span style={{
                          marginLeft: 6, fontSize: '0.62rem', color: PALETTE.warn, fontWeight: 700,
                          background: PALETTE.warn + '22', padding: '1px 5px', borderRadius: 3,
                          textTransform: 'none', letterSpacing: 0,
                        }}>fallback</span>
                      )}
                    </div>
                    <div style={{ color: PALETTE.text, fontSize: '0.82rem', lineHeight: 1.4 }}>
                      "{dialog}"
                    </div>
                    {(s.missingLabels || []).length > 0 && (
                      <div style={{ color: PALETTE.error, fontSize: '0.7rem', marginTop: 4 }}>
                        missing on screen: {(s.missingLabels || []).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {surfacedBugs.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <SectionHeader>Surfaced bugs ({surfacedBugs.length})</SectionHeader>
              <ul style={{ paddingLeft: 16, color: PALETTE.text, fontSize: '0.82rem', margin: 0 }}>
                {surfacedBugs.map((b, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    <span style={{
                      fontSize: '0.7rem', color: PALETTE.warn, fontWeight: 700, marginRight: 6,
                    }}>{b.severity || 'P?'}</span>
                    <span style={{ color: PALETTE.muted }}>{b.sceneName}:</span>{' '}
                    {b.description}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{
            background: PALETTE.panelAlt, padding: 12, borderRadius: 6,
            border: `1px dashed ${PALETTE.border}`, color: PALETTE.dim, fontSize: '0.78rem',
          }}>
            Promote / Discard buttons land in Phase TM-5 once the Cloud Functions ship.
          </div>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div style={{ color: PALETTE.dim, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ color: PALETTE.text }}>{value ?? '—'}</div>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div style={{
      color: PALETTE.muted, fontSize: '0.72rem', textTransform: 'uppercase',
      letterSpacing: '0.06em', fontWeight: 700, marginBottom: 8,
    }}>{children}</div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────────
export default function AdminTMHubPage() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [machineFilter, setMachineFilter] = useState('all');
  const [scenarioFilter, setScenarioFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);

  // --- Auth gate ---
  useEffect(() => {
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u && (ADMIN_UIDS.includes(u.uid) || (await u.getIdTokenResult()).claims?.role === 'admin')) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
      setAuthChecked(true);
    });
    return () => unsub();
  }, []);

  // --- Firestore fetch ---
  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const db = getFirestore();
      const q = query(collection(db, 'tm_runs'), orderBy('createdAt', 'desc'), limit(200));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRuns(data);
      // If the previously-selected run got refreshed, swap in the fresh copy
      if (selected) {
        const fresh = data.find(r => r.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (err) {
      console.error('Failed to load tm_runs:', err);
      setError(err.message || 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin, refresh]);

  // --- Render: auth states ---
  if (!authChecked) {
    return <div style={{ padding: 40, color: PALETTE.text, background: PALETTE.bg, minHeight: '100vh' }}>Checking auth…</div>;
  }
  if (!user) {
    return (
      <div style={{ padding: 40, color: PALETTE.text, background: PALETTE.bg, minHeight: '100vh' }}>
        <h1>Sign-in required</h1>
        <p>This page is admin-only. <Link to="/login">Sign in</Link>.</p>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div style={{ padding: 40, color: PALETTE.text, background: PALETTE.bg, minHeight: '100vh' }}>
        <h1>Access denied</h1>
        <p>You're signed in as {user.email || user.uid} but this page is admin-only.</p>
        <p><Link to="/admin">← Admin home</Link></p>
      </div>
    );
  }

  // --- Filtered list ---
  const machineNames = Array.from(new Set(runs.map(r => r.machineName).filter(Boolean))).sort();
  const scenarioIds = Array.from(new Set(runs.map(r => r.scenarioId).filter(Boolean))).sort();
  const statusPredicate = STATUS_FILTERS.find(f => f.id === statusFilter)?.predicate || (() => true);
  const filteredRuns = runs.filter(r =>
    statusPredicate(r) &&
    (machineFilter === 'all' || r.machineName === machineFilter) &&
    (scenarioFilter === 'all' || r.scenarioId === scenarioFilter)
  );

  return (
    <div className="mp-page" style={{ background: PALETTE.bg, color: PALETTE.text, minHeight: '100vh' }}>
      <section style={{ maxWidth: 1600, margin: '0 auto', padding: '32px 24px' }}>
        {/* --- Header --- */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h1 style={{ color: PALETTE.text, fontSize: '1.8rem', fontWeight: 800, margin: 0 }}>Testing & Marketing Hub</h1>
            <p style={{ color: PALETTE.dim, margin: '4px 0 0 0' }}>
              Demo runs across all Macs. Each run captures scene-by-scene QA + 6 sampled thumbnails.
            </p>
          </div>
          <button onClick={refresh} disabled={loading} style={{
            padding: '8px 16px', background: PALETTE.accent, color: 'white', border: 'none',
            borderRadius: 6, cursor: loading ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 600,
            opacity: loading ? 0.5 : 1,
          }}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: '0.82rem' }}>
          <Link to="/admin" style={{ color: PALETTE.accent }}>← Admin home</Link>
          <Link to="/admin/template-candidates" style={{ color: PALETTE.accent }}>Template Candidates</Link>
          <Link to="/admin/hub" style={{ color: PALETTE.accent }}>Hub Cleanup</Link>
        </div>

        {error && (
          <div style={{ padding: 12, background: '#3f1d1d', color: PALETTE.error, borderRadius: 6, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* --- Status filter tabs --- */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: `1px solid ${PALETTE.border}`, flexWrap: 'wrap' }}>
          {STATUS_FILTERS.map(f => {
            const count = runs.filter(f.predicate).length;
            return (
              <button key={f.id} onClick={() => { setStatusFilter(f.id); setSelected(null); }} style={{
                padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                borderRadius: '6px 6px 0 0',
                background: statusFilter === f.id ? PALETTE.panel : 'transparent',
                color: statusFilter === f.id ? PALETTE.text : PALETTE.dim,
              }}>{f.label} <span style={{ color: PALETTE.dim }}>({count})</span></button>
            );
          })}
        </div>

        {/* --- Secondary filters: machine + scenario --- */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, fontSize: '0.82rem', alignItems: 'center' }}>
          <label style={{ color: PALETTE.muted }}>
            Machine{' '}
            <select value={machineFilter} onChange={(e) => setMachineFilter(e.target.value)} style={selectStyle}>
              <option value="all">All ({machineNames.length})</option>
              {machineNames.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label style={{ color: PALETTE.muted }}>
            Scenario{' '}
            <select value={scenarioFilter} onChange={(e) => setScenarioFilter(e.target.value)} style={selectStyle}>
              <option value="all">All ({scenarioIds.length})</option>
              {scenarioIds.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <span style={{ color: PALETTE.dim, marginLeft: 'auto' }}>{filteredRuns.length} runs shown</span>
        </div>

        {/* --- Two-column: list + detail --- */}
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 20 }}>
          <div>
            {filteredRuns.length === 0 && !loading && (
              <div style={{
                padding: 40, background: PALETTE.panel, borderRadius: 8, color: PALETTE.dim,
                textAlign: 'center', border: `1px dashed ${PALETTE.border}`,
              }}>
                {runs.length === 0
                  ? "No runs yet. Run scripts/marketing/record-and-narrate.sh <scenario> 'iPhone 17 Pro' on a Mac to seed the first one."
                  : "No runs match the current filters."}
              </div>
            )}

            {filteredRuns.map(run => (
              <RunRow
                key={run.id} run={run}
                isSelected={selected?.id === run.id}
                onClick={() => setSelected(run)}
              />
            ))}
          </div>

          {selected && <DetailDrawer run={selected} onClose={() => setSelected(null)} />}
        </div>
      </section>
    </div>
  );
}

const selectStyle = {
  background: PALETTE.panel, color: PALETTE.text, border: `1px solid ${PALETTE.border}`,
  padding: '4px 8px', borderRadius: 4, fontSize: '0.82rem', marginLeft: 4,
};

function RunRow({ run, isSelected, onClick }) {
  const score = run.qaScore;
  return (
    <div
      onClick={onClick}
      style={{
        background: isSelected ? '#243149' : PALETTE.panel,
        borderRadius: 8, padding: 12, marginBottom: 8, cursor: 'pointer',
        border: `1px solid ${isSelected ? PALETTE.accent : PALETTE.border}`,
        display: 'grid', gridTemplateColumns: '1fr auto auto auto auto auto', gap: 12, alignItems: 'center',
      }}
    >
      <div>
        <div style={{ color: PALETTE.text, fontWeight: 600, fontSize: '0.95rem' }}>{run.scenarioId || '(unknown)'}</div>
        <div style={{ color: PALETTE.dim, fontSize: '0.72rem' }}>{run.flowName}</div>
      </div>
      <span style={pillStyle(PALETTE.panelAlt, PALETTE.muted)}>{run.machineName || '?'}</span>
      <span style={pillStyle(PALETTE.panelAlt, PALETTE.dim)}>{run.appBuild || '?'}</span>
      <span style={pillStyle(qaScoreColor(score) + '22', qaScoreColor(score))}>
        {score != null ? `${Math.round(score * 100)}%` : '—'}
      </span>
      <span style={pillStyle(statusColor(run.status) + '22', statusColor(run.status))}>
        {run.status || '?'}
      </span>
      <span style={{ color: PALETTE.dim, fontSize: '0.75rem' }}>{formatTimestamp(run.createdAt)}</span>
    </div>
  );
}

function pillStyle(bg, fg) {
  return {
    background: bg, color: fg, padding: '3px 10px', borderRadius: 999,
    fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap',
  };
}
